import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@babackapp/ui/components/dialog";
import { Label } from "@babackapp/ui/components/label";
import {
  RadioGroup,
  RadioGroupItem,
} from "@babackapp/ui/components/radio-group";
import type { UseMutationResult } from "@tanstack/react-query";
import { useEffect, useState } from "react";

type ShippingRate = {
  handle: string;
  title: string;
  price: string;
};

type RepairShippingBody = {
  shippingLineTitle: string;
  shippingLinePrice: number;
  currencyCode: string;
};

type RepairShippingDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availableRates: ShippingRate[];
  currencyCode: string;
  repairMutation: UseMutationResult<unknown, unknown, RepairShippingBody>;
  onRepairSuccess?: () => void;
};

export function RepairShippingDialog({
  open,
  onOpenChange,
  availableRates,
  currencyCode,
  repairMutation,
  onRepairSuccess,
}: RepairShippingDialogProps) {
  const [selectedHandle, setSelectedHandle] = useState<string>(
    availableRates[0]?.handle ?? "",
  );

  // Reset selection and mutation state on each open/close cycle.
  useEffect(() => {
    if (open) {
      setSelectedHandle(availableRates[0]?.handle ?? "");
    } else {
      repairMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selectedRate = availableRates.find((r) => r.handle === selectedHandle);

  function handleApply() {
    if (!selectedRate) return;
    repairMutation.mutate(
      {
        shippingLineTitle: selectedRate.title,
        shippingLinePrice: parseFloat(selectedRate.price),
        currencyCode,
      },
      {
        onSuccess: () => {
          onRepairSuccess?.();
          onOpenChange(false);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Corriger la livraison</DialogTitle>
        </DialogHeader>

        {availableRates.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Aucune méthode de livraison disponible pour cette commande.
          </p>
        ) : (
          <RadioGroup
            value={selectedHandle}
            onValueChange={setSelectedHandle}
            className="gap-3"
          >
            {availableRates.map((rate) => (
              <div
                key={rate.handle}
                className="flex items-center gap-3 rounded-md border p-3 cursor-pointer hover:bg-accent"
                onClick={() => setSelectedHandle(rate.handle)}
              >
                <RadioGroupItem
                  value={rate.handle}
                  id={`rate-${rate.handle}`}
                />
                <Label
                  htmlFor={`rate-${rate.handle}`}
                  className="flex-1 cursor-pointer"
                >
                  <span className="font-medium">{rate.title}</span>
                  <span className="ml-2 text-muted-foreground text-xs">
                    {rate.price} {currencyCode}
                  </span>
                </Label>
              </div>
            ))}
          </RadioGroup>
        )}

        {repairMutation.isError && (
          <p className="text-sm text-destructive">
            Une erreur est survenue lors de la correction.
          </p>
        )}

        {repairMutation.isSuccess && (
          <p className="text-sm text-green-600">
            Livraison corrigée avec succès.
          </p>
        )}

        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-md border px-4 py-2 text-sm font-medium hover:bg-accent transition-colors"
            disabled={repairMutation.isPending}
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={
              !selectedRate ||
              repairMutation.isPending ||
              availableRates.length === 0
            }
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/70 transition-colors disabled:opacity-50 disabled:pointer-events-none"
          >
            {repairMutation.isPending ? "Application…" : "Appliquer"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
